<template>
  <div>
    <general-table
      ref="generalTable"
      :api-url="apiUrl"
      :add-component-name="addComponentName"
      :edit-component="editComponent"
      :delete-content="true"
      :block-content="false"
      :edit-content="true"
      :view-content="false"
      :add-type="addType"
      :columns="columns"
      :type="type"
    >
      <template #cell(department)="data">
        <div
          v-if="data.item.department"
          class="d-flex flex-column"
        >
          <span>{{ data.item.department.title.ar }}</span>
          <span>{{ data.item.department.title.en }}</span>
        </div>
      </template>

      <template #cell(department_head)="data">
        <div class="d-flex py-1 px-1">
          <div style="margin-right:10px ;">
            <b-avatar size="40" />
          </div>
          <div class="d-flex flex-column justify-content-start align-items-start">
            <span>{{ data.item.department_head.name }}</span>
            <span class="text-muted">{{ data.item.department_head.email }}</span>
          </div>
        </div>
      </template>

      <template #cell(auditors)="data">
        <div
          class="d-flex flex-wrap align-items-center px-1"
          style="min-width: 150px"
        >
          <template v-if="Array.isArray(data.item.auditors) && data.item.auditors.length > 0">
            <!-- Show first 4 auditors -->
            <div
              v-for="(auditor, index) in data.item.auditors.slice(0, 4)"
              :key="auditor.id || index"
              :style="{ marginLeft: index !== 0 ? '-5px' : '0' }"
            >
              <span
                v-b-popover.hover.top.html="getSpan(auditor.name, auditor.email)"
              >
                <b-avatar
                  class="hover-avatar"
                  size="28"
                  :style="{
                    backgroundColor: colors[index % colors.length],
                    color: 'white',
                  }"
                  :text="getInitials(auditor.name)"
                />
              </span>
            </div>

            <!-- Show "+X" avatar if more than 4 auditors -->
            <div
              v-if="data.item.auditors.length > 4"
              :style="{ marginLeft: '-5px' }"
            >
              <span
                v-b-popover.hover.right.html="getRemainingAuditorsPopover(data.item.auditors.slice(4))"
              >
                <b-avatar
                  size="28"
                  variant="light-secondary"
                  class="font-weight-bold"
                >
                  +{{ data.item.auditors.length - 4 }}
                </b-avatar>
              </span>
            </div>
          </template>

          <!-- Fallback if auditors is not a valid array -->
          <template v-else>
            <span class="text-muted small">No auditors</span>
          </template>
        </div>
      </template>

      <template #cell(is_completed)="data">
        <IsCompletedCell
          :data="data"
          :get-all-data="getAllData"
        />
      </template>

      <template #cell(final_statement)="data">
        <FinalStatementCell
          :data="data"
          :get-all-data="getAllData"
        />
      </template>
      <template #cell(findings)="data">
        <FindingsAuditCell
          :data="data"
          :get-all-data="getAllData"
        />
      </template>
    </general-table>
  </div>
</template>

<script>
import GeneralTable from '@/components/general-table/GeneralTable.vue'
import FinalStatementCell from './control-internal-audit/FinalStatementCell.vue'
import IsCompletedCell from './control-internal-audit/IsCompletedCell.vue'
import FindingsAuditCell from './control-internal-audit/FindingsAuditCell.vue'

export default {
  components: {
    GeneralTable, IsCompletedCell, FinalStatementCell, FindingsAuditCell,
  },
  data() {
    return {
      APIURL: 'internal-audit',
      addType: 'Add Internal Audit',
      addComponentName: 'add-internal-audit',
      editComponent: 'edit-internal-audit',
      viewContent: true,
      type: 'page',
      partners: [],
      filter: {},
      types: '',
      columns: [
        { key: 'id', sortable: true },
        { key: 'audit_type', label: 'audit type' },
        { key: 'auditors', label: 'auditors' },
        { key: 'audit_details', label: 'audit details' },
        { key: 'grading', label: 'grading' },
        { key: 'department', label: 'department' },
        { key: 'department_head', label: 'department head' },
        { key: 'findings', label: 'findings' },
        { key: 'final_statement', label: 'final statement' },
        { key: 'is_completed', label: 'Completed' },
        { key: 'actions' },
      ],
      colors: [
        '#FF5733', // red-orange
        '#33B5FF', // blue
        '#28A745', // green
        '#FFC107', // amber
        '#6F42C1', // purple
        '#E83E8C', // pink
        '#20C997', // teal
        '#FD7E14', // orange
      ],
    }
  },

  computed: {
    apiUrl() {
      let base = 'internal-audit?'

      if (!Object.values(this.filter).length) {
        return base.slice(0, -1)
      }

      // eslint-disable-next-line no-restricted-syntax
      for (const [key, value] of Object.entries(this.filter)) {
        if (key && value) {
          base += `${key}=${value}&`
        }
      }
      // console.log(base.slice(0, -1))
      return base.slice(0, -1)
    },
  },

  methods: {
    getInitials(fullName) {
      if (!fullName) return ''
      const str = String(fullName).trim()
      if (!str) return ''
      const parts = str.split(/\s+/)
      if (parts.length === 1) {
        return parts[0][0]?.toUpperCase() || ''
      }
      return (parts[0][0] + parts[1][0]).toUpperCase()
    },
    getSpan(name, email) {
      return `<div style="text-align: start; color: black">${name}</div>
    <div style="text-align: start; color: gray">${email}</div>`
    },
    getRemainingAuditorsPopover(auditors) {
    // Build HTML popover with all remaining auditors
      return auditors
        .map(
          auditor => `
        <div style="text-align:start; margin-bottom:4px;">
          <div style="color:black; font-weight:500;">${auditor.name}</div>
          <div style="color:gray;">${auditor.email}</div>
        </div>
      `,
        )
        .join('')
    },
    getAllData() {
      this.$refs.generalTable.getAllData(1)
    },
  },
}
</script>

<style>
.avatar-wrapper {
  transition: all 0.2s ease;
}

.hover-avatar {
  transition: transform 0.2s ease, z-index 0.2s ease;
  cursor: pointer;
  position: relative;
  z-index: 1;
}

.hover-avatar:hover {
  transform: translateY(-5px);
  z-index: 10;
}
</style>
